import React from "react";
import Grid from "@mui/material/Grid";
import {
  SectionHeader,
  InputField,
  PhoneField,
  PhoneExtensionField,
  EmailField,
  ReadOnlyField,
  Checkbox,
  InfoNote
} from "../primitives";
import { selectionKey } from "../data";
import { useValidationError } from "../validation";
import { colors } from "../../../utils/styles";

function CertificationStep({
  selection,
  subagenciesBySelection = {},
  entries,
  prep,
  onPrepChange,
  confirm,
  onConfirmChange,
  submissionDate,
  submissionId,
  submitted
}) {
  const subs = subagenciesBySelection[selectionKey(selection)] || [];
  const filledEntries = entries.filter((e) => e.subagency);
  const filledCount = filledEntries.length;
  const confirmError = useValidationError("confirm");

  return (
    <>
      <SectionHeader
        number="4"
        title="Certification & Submission"
        subtitle="Form preparer information and submission confirmation"
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {filledCount > 0 && (
          <InfoNote icon={false}>
            <p style={{ margin: "0 0 8px" }}>
              Before submitting, review <strong>Step 2 - Direct Charges</strong> to ensure all intended
              subagencies have been added.
            </p>
            <p style={{ margin: "0 0 8px" }}>
              Subagencies included in this submission <strong>({filledCount})</strong>:
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {filledEntries.map((entry) => {
                const sub = subs.find((s) => s.code === entry.subagency);
                return (
                  <div
                    key={entry.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 13,
                      color: colors.text
                    }}
                  >
                    <div
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: colors.success,
                        flexShrink: 0
                      }}
                    />
                    <span style={{ fontWeight: 600 }}>{sub?.code}</span>
                    <span style={{ color: colors.textMuted }}>—</span>
                    <span>{sub?.name}</span>
                    {entry.chargeCategory && (
                      <span style={{ color: colors.textMuted, fontSize: 11 }}>({entry.chargeCategory})</span>
                    )}
                  </div>
                );
              })}
            </div>
          </InfoNote>
        )}

        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputField
              label="Form Preparer First Name"
              required
              path="preparer.first"
              value={prep.first}
              onChange={(e) => onPrepChange("first", e.target.value)}
              placeholder="First name"
              maxLength={100}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputField
              label="Form Preparer Last Name"
              required
              path="preparer.last"
              value={prep.last}
              onChange={(e) => onPrepChange("last", e.target.value)}
              placeholder="Last name"
              maxLength={100}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3.5 }}>
            <PhoneField
              label="Form Preparer Phone"
              required
              path="preparer.phone"
              value={prep.phone}
              onChange={(e) => onPrepChange("phone", e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 2.5 }}>
            <PhoneExtensionField
              label="Phone Extension"
              path="preparer.phoneExtension"
              value={prep.phoneExtension || ""}
              onChange={(e) => onPrepChange("phoneExtension", e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <EmailField
              label="Form Preparer Email"
              required
              path="preparer.email"
              value={prep.email}
              onChange={(e) => onPrepChange("email", e.target.value)}
              maxLength={200}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <ReadOnlyField label="Submission Date" value={submissionDate} accent />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <ReadOnlyField label="Submission ID" value={submissionId} accent />
          </Grid>
        </Grid>

        <div
          data-error-path={confirmError ? "confirm" : undefined}
          style={{
            padding: 20,
            background: colors.sectionBg,
            borderRadius: 8,
            border: confirmError ? `1px solid ${colors.error}` : "1px solid transparent"
          }}
        >
          <p style={{ margin: "0 0 14px", fontSize: 13, fontWeight: 600, color: colors.navy }}>
            Send Confirmation To:
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Checkbox
              label="Form Preparer"
              checked={confirm.preparer}
              onChange={(e) => onConfirmChange("preparer", e.target.checked)}
              disabled={submitted}
            />
            <Checkbox
              label="Contacts"
              checked={confirm.contacts}
              onChange={(e) => onConfirmChange("contacts", e.target.checked)}
              disabled={submitted}
            />
            <Checkbox
              label="Authorized Signer"
              checked={confirm.signer}
              onChange={(e) => onConfirmChange("signer", e.target.checked)}
              disabled={submitted}
            />
          </div>
          {confirmError && (
            <p style={{ margin: "10px 0 0", fontSize: 12, color: colors.error }}>{confirmError.message}</p>
          )}
        </div>

        <InfoNote variant="warning">
          <p style={{ margin: 0, marginBottom: 0 }}>
            After submission, you may still click the <strong>Previous</strong> button to review the submitted
            data. However, any <strong>changes made after submission will not be processed</strong> during the
            current session. To edit or resubmit information, you must start a <strong>new session</strong>{" "}
            using the link sent to your email.
          </p>
          <p style={{ margin: 0 }}>
            Once the form has been submitted, selected parties will receive a confirmation email that includes
            a <strong>report of the submitted data</strong>.
          </p>
        </InfoNote>
      </div>
    </>
  );
}

export default React.memo(CertificationStep);
